import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const PROMPT_MIN_ROWS = 2;
const PROMPT_MAX_ROWS = 6;

const PromptPanel = ({
  prompt = '',
  onPromptChange,
  onSubmitPrompt,
  onApplyPromptResult,
  onCancelPromptResult,
  canSubmit = true,
  onGenerate,
  loading = false,
  hasImage = false,
  executionTime = null,
}) => {
  const [messages, setMessages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [applyingMessageId, setApplyingMessageId] = useState(null);
  const promptTextareaRef = useRef(null);
  const trimmedPrompt = prompt.trim();
  const hasPendingImageActions = messages.some(
    (message) => message.type === 'image_result' && message.showActions,
  );
  const isBusy = submitting || loading || applyingMessageId != null;
  const inputDisabled = isBusy || hasPendingImageActions;

  useLayoutEffect(() => {
    const el = promptTextareaRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const verticalPadding =
      parseFloat(style.paddingTop) +
      parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) +
      parseFloat(style.borderBottomWidth);
    const minHeight = lineHeight * PROMPT_MIN_ROWS + verticalPadding;
    const maxHeight = lineHeight * PROMPT_MAX_ROWS + verticalPadding;

    el.style.height = 'auto';
    const contentHeight = el.scrollHeight;
    const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [prompt]);

  useEffect(
    () => () => {
      messages.forEach((message) => {
        if (message.revokeOnUnmount && message.chatImageUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(message.chatImageUrl);
        }
      });
    },
    [messages],
  );

  const handleSubmit = async () => {
    if (!trimmedPrompt || inputDisabled || !canSubmit) return;

    const userText = prompt;
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', type: 'text', text: userText },
    ]);
    onPromptChange?.('');

    if (!onSubmitPrompt) return;

    setSubmitting(true);
    try {
      const response = await onSubmitPrompt(userText);
      if (response?.type === 'image_result' && response.imageUrl) {
        const chatImageUrl = response.chatImageUrl || response.imageUrl;
        setMessages((prev) => [
          ...prev,
          {
            id: `ai-${Date.now()}`,
            role: 'ai',
            type: 'image_result',
            imageUrl: response.imageUrl,
            chatImageUrl,
            revokeOnUnmount: chatImageUrl.startsWith('blob:'),
            resultS3Key: response.resultS3Key,
            showActions: true,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: `ai-${Date.now()}`,
            role: 'ai',
            type: 'text',
            text: response?.text || 'No response received.',
          },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-err-${Date.now()}`,
          role: 'ai',
          type: 'text',
          text: err?.message || 'Failed to run photorealistic prompt.',
        },
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportResultImage = async (message) => {
    const url = message?.chatImageUrl || message?.imageUrl;
    if (!url) return;
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `photorealistic-prompt-${Date.now()}.png`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCancelResult = (messageId) => {
    setMessages((prev) => {
      const target = prev.find((message) => message.id === messageId);
      onCancelPromptResult?.(target?.resultS3Key);
      return prev.map((message) =>
        message.id === messageId ? { ...message, showActions: false } : message,
      );
    });
  };

  const handleApplyResult = async (message) => {
    if (!message?.resultS3Key || !message?.imageUrl || !onApplyPromptResult) return;
    setApplyingMessageId(message.id);
    try {
      await onApplyPromptResult({
        resultS3Key: message.resultS3Key,
        imageUrl: message.imageUrl,
      });
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id ? { ...item, showActions: false, applied: true } : item,
        ),
      );
    } catch (err) {
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? {
                ...item,
                type: 'text',
                text: err?.message || 'Failed to apply result image.',
                showActions: false,
              }
            : item,
        ),
      );
    } finally {
      setApplyingMessageId(null);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-3 shrink-0">
        <h3 className="text-[16px] lao-mn text-gray-700">Prompt Edit</h3>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-2 py-2">
          <div className="max-w-[88%] rounded-2xl rounded-tl-md border border-white bg-white/50 px-3 py-2 text-sm leading-relaxed text-gray-700">
            Describe the scene or style you want. I&apos;ll use your prompt when generating the image.
          </div>

          {messages.map((message) =>
            message.role === 'user' ? (
              <div key={message.id} className="flex justify-end">
                <div className="max-w-[88%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-md bg-white px-3 py-2 text-sm leading-relaxed text-gray-700">
                  {message.text}
                </div>
              </div>
            ) : message.type === 'image_result' ? (
              <div key={message.id} className="flex justify-start">
                <div className="max-w-full rounded-2xl rounded-tl-md border border-white bg-white/50 p-2">
                  <div className="relative">
                    <img
                      src={message.chatImageUrl || message.imageUrl}
                      alt="AI photorealistic result"
                      className="max-h-40 w-full rounded-lg object-contain"
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        if (message.imageUrl && e.currentTarget.src !== message.imageUrl) {
                          e.currentTarget.src = message.imageUrl;
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => void handleExportResultImage(message)}
                      className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200/80 bg-white/90 text-gray-700 shadow-sm transition-colors hover:bg-white hover:text-gray-900"
                      title="Export image"
                      aria-label="Export image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  </div>
                  {message.showActions ? (
                    <div className="mt-2 flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => handleCancelResult(message.id)}
                        disabled={applyingMessageId === message.id}
                        className="flex flex-1 flex-row items-center justify-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-300 transition-colors hover:border-primary-300 hover:bg-primary-500 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleApplyResult(message)}
                        disabled={applyingMessageId === message.id}
                        className="flex flex-1 flex-row items-center justify-center gap-1 rounded-full border border-gray-800 bg-gray-800 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {applyingMessageId === message.id ? 'Applying...' : 'Apply'}
                      </button>
                    </div>
                  ) : message.applied ? (
                    <p className="mt-2 text-center text-[11px] text-gray-500">Applied to canvas</p>
                  ) : null}
                </div>
              </div>
            ) : (
              <div key={message.id} className="flex justify-start">
                <div className="max-w-[88%] whitespace-pre-wrap break-words rounded-2xl rounded-tl-md border border-white bg-white/50 px-3 py-2 text-sm leading-relaxed text-gray-700">
                  {message.text}
                </div>
              </div>
            ),
          )}

          {submitting ? (
            <div className="flex w-fit items-center gap-1 rounded-2xl rounded-tl-md bg-gray-100 px-3 py-2.5">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" />
            </div>
          ) : null}

          {executionTime != null && !isBusy ? (
            <p className="px-1 text-center text-[11px] text-gray-400">
              Generated in {executionTime.toFixed(0)} ms
            </p>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-gray-300 pt-4 mt-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-2 py-2 shadow-sm focus-within:border-gray-300">
            <textarea
              ref={promptTextareaRef}
              id="photorealistic-prompt"
              value={prompt}
              onChange={(e) => onPromptChange?.(e.target.value)}
              placeholder={
                hasPendingImageActions
                  ? 'Apply or cancel the result above to continue...'
                  : 'Type Your Prompt...'
              }
              rows={PROMPT_MIN_ROWS}
              disabled={inputDisabled}
              className="w-full resize-none overflow-hidden border-0 bg-transparent px-1 py-1 text-sm leading-5 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:opacity-60"
            />
            <div className="flex items-center justify-between px-0.5 pt-1">
              <button
                type="button"
                disabled={inputDisabled}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200/80 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                title="Voice input"
                aria-label="Voice input"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" x2="12" y1="19" y2="22" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!trimmedPrompt || inputDisabled || !canSubmit}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200/80 disabled:cursor-not-allowed disabled:opacity-40"
                title="Send"
                aria-label="Send message"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6.0714 3.6307C6.47007 2.46403 8.08207 2.4287 8.55474 3.5247L8.59474 3.63136L9.13274 5.2047C9.25603 5.56552 9.45527 5.8957 9.71702 6.17298C9.97877 6.45026 10.2969 6.66817 10.6501 6.81203L10.7947 6.86603L12.3681 7.40337C13.5347 7.80203 13.5701 9.41403 12.4747 9.8867L12.3681 9.9267L10.7947 10.4647C10.4338 10.5879 10.1035 10.7871 9.82608 11.0489C9.54869 11.3106 9.33067 11.6288 9.18674 11.982L9.13274 12.126L8.5954 13.7C8.19674 14.8667 6.58474 14.902 6.11274 13.8067L6.0714 13.7L5.53407 12.1267C5.41086 11.7658 5.21165 11.4354 4.9499 11.158C4.68814 10.8806 4.36993 10.6626 4.01674 10.5187L3.87274 10.4647L2.2994 9.92736C1.13207 9.5287 1.09674 7.9167 2.19274 7.4447L2.2994 7.40337L3.87274 6.86603C4.23356 6.74274 4.56374 6.54349 4.84102 6.28174C5.11829 6.02 5.33621 5.70183 5.48007 5.3487L5.53407 5.2047L6.0714 3.6307ZM7.3334 4.06136L6.79607 5.6347C6.60833 6.1849 6.30292 6.68757 5.90109 7.1077C5.49927 7.52782 5.0107 7.85531 4.4694 8.06736L4.30274 8.12803L2.7294 8.66536L4.30274 9.2027C4.85294 9.39044 5.35561 9.69585 5.77573 10.0977C6.19586 10.4995 6.52335 10.9881 6.7354 11.5294L6.79607 11.696L7.3334 13.2694L7.87074 11.696C8.05848 11.1458 8.36389 10.6432 8.76571 10.223C9.16753 9.80291 9.65611 9.47542 10.1974 9.26336L10.3641 9.20336L11.9374 8.66536L10.3641 8.12803C9.81387 7.94029 9.3112 7.63488 8.89107 7.23306C8.47094 6.83123 8.14345 6.34266 7.9314 5.80136L7.8714 5.6347L7.3334 4.06136ZM12.6667 1.33203C12.7915 1.33203 12.9137 1.36702 13.0195 1.43301C13.1253 1.49901 13.2105 1.59337 13.2654 1.70536L13.2974 1.78336L13.5307 2.46736L14.2154 2.7007C14.3404 2.74316 14.45 2.82177 14.5302 2.92657C14.6105 3.03137 14.6578 3.15764 14.6663 3.28938C14.6747 3.42111 14.6438 3.55239 14.5775 3.66656C14.5113 3.78073 14.4126 3.87266 14.2941 3.9307L14.2154 3.9627L13.5314 4.19603L13.2981 4.8807C13.2555 5.00565 13.1769 5.11516 13.072 5.19536C12.9672 5.27556 12.8409 5.32283 12.7092 5.33118C12.5774 5.33953 12.4462 5.30859 12.3321 5.24228C12.2179 5.17597 12.1261 5.07728 12.0681 4.9587L12.0361 4.8807L11.8027 4.1967L11.1181 3.96336C10.9931 3.9209 10.8835 3.84229 10.8033 3.73749C10.723 3.63269 10.6756 3.50642 10.6672 3.37468C10.6588 3.24295 10.6897 3.11168 10.7559 2.99751C10.8222 2.88334 10.9208 2.79141 11.0394 2.73336L11.1181 2.70136L11.8021 2.46803L12.0354 1.78336C12.0804 1.65165 12.1654 1.5373 12.2786 1.45636C12.3918 1.37542 12.5276 1.33195 12.6667 1.33203Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onGenerate}
            disabled={loading || !hasImage}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
          >
            {loading ? 'Processing...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptPanel;
